import { useState } from "react";
import { PageHeader, Loading, ExportButton } from "@/components/Common";
import { MonthPicker, PastMonthBanner } from "@/components/MonthView";
import { FilterBar, useRowFilter } from "@/components/Filters";
import { MovementBadge } from "@/components/Badges";
import { useLoad } from "@/hooks/useData";
import { api } from "@/lib/api";
import { monthLabel, monthStr } from "@/lib/format";

const heat = (v, max) => {
  if (!v) return "transparent";
  return `rgba(244, 20, 68, ${Math.min(0.08 + (v / max) * 0.55, 0.65)})`;
};

export default function SalesAnalysis() {
  const [month, setMonth] = useState(monthStr());
  const { data } = useLoad(() => api.get("/report/sales-forecast", { params: { month } }).then((r) => r.data), [month]);
  const f = useRowFilter(data?.rows);
  if (!data) return <Loading />;
  const hm = data.history_months;
  const cur = monthLabel(data.month);
  return (
    <div>
      <PageHeader title="Sales & Forecast" subtitle={`Rolling 12 months (${monthLabel(hm[0])} – ${monthLabel(hm[11])}) + ${cur} ${data.is_current ? "running" : ""}. Shifts automatically on the 1st of every month.`}>
        <MonthPicker month={month} setMonth={setMonth} />
        <ExportButton kind="sales_analysis" params={{ month }} />
      </PageHeader>
      <PastMonthBanner data={data} />
      <FilterBar f={f} total={data.rows.length} />
      <div className="panel overflow-auto max-h-[calc(100vh-260px)]">
        <table className="data-table" data-testid="sales-analysis-table">
          <thead><tr>
            <th className="sticky left-0 z-20 bg-card">Product</th>
            {hm.map((m) => <th key={m} className="text-right">{monthLabel(m)}</th>)}
            <th className="text-right text-primary" data-testid="current-month-header">{cur} {data.is_current ? "(running)" : "(closed)"}</th>
            <th className="text-right">Yearly</th><th className="text-right">Avg / mo</th>
            <th className="text-right">Avg daily</th><th>Movement</th>
            <th className="text-right border-l border-primary/30">Base</th><th className="text-right">Pace</th><th className="text-right text-primary">Forecast 30d</th>
            <th className="text-right">Req. stock 30d</th><th className="text-right">Order qty</th><th className="min-w-[220px]">Flags</th>
          </tr></thead>
          <tbody>
            {f.filtered.map((r) => {
              const max = Math.max(1, ...hm.map((m) => r.monthly_sales[m]));
              return (
                <tr key={r.id} data-testid={`sales-row-${r.sku}`}>
                  <td className="sticky left-0 z-[5] bg-card max-w-[280px]"><div className="truncate text-xs font-medium" title={r.name}>{r.name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.sku}</div></td>
                  {hm.map((m) => <td key={m} className="num" style={{ background: heat(r.monthly_sales[m], max) }}>{r.monthly_sales[m]}</td>)}
                  <td className="num font-bold border-x border-primary/30">{r.current_month_sale}</td>
                  <td className="num font-bold">{r.yearly_sales}</td>
                  <td className="num">{r.avg_monthly}</td>
                  <td className="num text-muted-foreground" data-testid={`avg-daily-${r.sku}`}>{r.avg_daily.toFixed(2)}</td>
                  <td><MovementBadge value={r.movement} /></td>
                  <td className="num border-l border-primary/30" data-testid={`fc-base-${r.sku}`}>{r.fc2_base.toFixed(1)}</td>
                  <td className="num" data-testid={`fc-pace-${r.sku}`}>{r.fc2_pace.toFixed(1)}</td>
                  <td className="num text-primary font-bold" data-testid={`fc-forecast-${r.sku}`}>{r.fc2_forecast_30d.toFixed(1)}</td>
                  <td className="num" data-testid={`fc-req-${r.sku}`}>{r.fc2_req_stock_30d}</td>
                  <td className="num font-bold" data-testid={`fc-order-${r.sku}`}>{r.fc2_order_qty}</td>
                  <td className="wrap-cell text-[11px] min-w-[220px]" data-testid={`fc-flags-${r.sku}`}>{r.fc2_flags}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
